/**
 * IMPORTS
 */
import {execute} from 'src/aggregates/execute';
import * as commands from 'src/aggregates/preferences/commands';
import * as handlers from 'src/aggregates/preferences/handlers';
import {fetch as fetchUser} from 'src/aggregates/user/selectors';


/**
 * CODE
 */

/**
 * I am an API to add an auto message.
 *
 * :param message: the message text to add
 *
 * :returns: promise with nothing
 */
async function addAutoMessage(message: string): Promise<void>
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    await execute(
        commands.addAutoMessage(message, user),
        handlers.addAutoMessage,
    );
}


/**
 * I am an API to disable the tour.
 *
 * :returns: promise with nothing
 */
async function disableTour(): Promise<void>
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    await execute(commands.disableTour(user), handlers.disableTour);
}


/**
 * I am an API to remove an auto message.
 *
 * :param message: the message id to remove
 *
 * :returns: promise with nothing
 */
async function removeAutoMessage(message: number): Promise<void>
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    await execute(
        commands.removeAutoMessage(message, user),
        handlers.removeAutoMessage,
    );
}


/**
 * I am an API for set current auto message.
 *
 * :param message: the message id to use
 *
 * :returns: promise with nothing
 */
async function setCurrentAutoMessage(message: number): Promise<void>
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    await execute(
        commands.setCurrentAutoMessage(message, user),
        handlers.setCurrentAutoMessage,
    );
}


/**
 * I am an API for set email page size.
 *
 * :param size: the size of email page
 *
 * :returns: promise with nothing
 */
async function setEmailPageSize(size: number): Promise<void>
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    await execute(
        commands.setEmailPageSize(size, user),
        handlers.setEmailPageSize,
    );
}


/**
 * I am an API to set user's last feedback timestamp.
 *
 * :param feedback: user's last feedback timestamp
 *
 * :returns: promise with nothing
 */
async function setFeedback (feedback: number): Promise<void>
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    await execute(
        commands.setFeedback(feedback, user),
        handlers.setFeedback,
    );
}


/**
 * I am an API to set a given tag color.
 *
 * :param tag: tag id to be updated
 * :param color: color to be updated for
 *
 * :returns: promise with nothing
 */
async function setTagColor (tag: number, color: string)
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    await execute(
        commands.setTagColor(color, tag, user),
        handlers.setTagColor,
    );
}


/**
 * I am an API for start preferences with default values.
 *
 * :returns: promise with nothing
 */
async function start(): Promise<void>
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    // start user preferences
    await execute(commands.start(user), handlers.start);

    // load old messages
    // FIXME: remove this load, all messages should be persisted by redux persist
    await execute(commands.load(user), handlers.load);
}


/**
 * I am an API to switch the mute notifications preference.
 *
 * :param muted: preference new state
 *
 * :returns: promise with nothing
 */
async function switchMuteNotifications(muted: boolean): Promise<void>
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    await execute(
        commands.switchMuteNotifications(user, muted),
        handlers.switchMuteNotifications,
    );
}


/**
 * I am an API to switch an user's paused status.
 *
 * :param paused: preference new state
 *
 * :returns: promise with nothing
 */
 async function switchPaused(paused: boolean): Promise<void>
 {
     // get logged user id as number
     const user = Number(fetchUser().id);
     
     // switch paused preference
     await execute(
         commands.switchPaused(user, paused),
         handlers.switchPaused,
     );
 }


/**
 * I am an API to update the wallet reminder.
 *
 * :param walletReminder: wallet reminder display state
 *
 * :returns: promise with nothing
 */
async function switchWalletReminder(walletReminder: boolean): Promise<void>
{
    // get logged user id as number
    const user = Number(fetchUser().id);

    await execute(
        commands.switchWalletReminder(user, walletReminder),
        handlers.switchWalletReminder,
    );
}


/**
 * EXPORTS
 */
export {
    addAutoMessage,
    disableTour,
    removeAutoMessage,
    setCurrentAutoMessage,
    setEmailPageSize,
    setFeedback,
    setTagColor,
    start,
    switchMuteNotifications,
    switchPaused,
    switchWalletReminder,
};
